import { IConnectionDrawingOptions } from '..';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	createStateFromTypeAndSubscribe,
	EntityType,
	ENTRY_POINT_TYPES,
	EntryPointEntityState,
	findNextAvailableName,
	getParentCallerEntity,
	IVariableDeclarationTransfer,
	PASS_THROUGH_CALLABLE_TYPES,
	PassThroughCallableEntityState,
	ProjectState,
	resolveEntityName,
	ValueWritingEntityState,
	VariableDeclarationState,
} from '@elyx-code/project-logic-tree';
import { getScopeOwner } from '@elyx-code/project-logic-tree';

export function createVariableDeclarationFromWritter(
	from: ValueWritingEntityState,
	_connectionOptions: IConnectionDrawingOptions,
	project: ProjectState,
	changeSet: ChangeSet
): VariableDeclarationState {
	const writterScopeOwner = getScopeOwner(from, project);

	const writterCaller = getParentCallerEntity(from);

	let altName: string | null = null;

	if (
		!!(
			from.type === EntityType.Operation ||
			from.type === EntityType.InternalCall
		) &&
		from.declaration?.type === EntityType.ActionDescriptor &&
		!!from.declaration.outputName
	) {
		altName = from.declaration.outputName;
	} else if (
		from.type === EntityType.FunctionCall &&
		from.declaration.implements?.type === EntityType.ActionDescriptor &&
		!!from.declaration.implements.outputName
	) {
		altName = from.declaration.implements.outputName;
	} else if (
		from.type === EntityType.Operation ||
		from.type === EntityType.InternalCall ||
		from.type === EntityType.FunctionCall
	) {
		altName = 'Main output of ' + resolveEntityName(from, project);
	}

	const desiredName = altName || resolveEntityName(from, project);

	// Ensure the name is unique in the scope of the writter
	const name = findNextAvailableName(
		desiredName,
		project.getVariableDeclarations()
	);

	// Create a new variable instance of this variable declaration at this coordinates
	const newVariableDeclarationTransfer: IVariableDeclarationTransfer =
		VariableDeclarationState.new(changeSet);
	newVariableDeclarationTransfer.name = name;
	newVariableDeclarationTransfer.calledByEntry =
		!!writterCaller && ENTRY_POINT_TYPES.includes(writterCaller.type)
			? [(writterCaller as EntryPointEntityState).toJSON()]
			: [];
	newVariableDeclarationTransfer.calledBySuccess =
		!!writterCaller && !ENTRY_POINT_TYPES.includes(writterCaller.type)
			? [(writterCaller as PassThroughCallableEntityState).toJSON()]
			: [];
	newVariableDeclarationTransfer.parent = writterScopeOwner.toReference();
	newVariableDeclarationTransfer.readsValue = from.toJSON();

	const newVariableDeclarationInstance = createStateFromTypeAndSubscribe(
		newVariableDeclarationTransfer,
		project
	) as VariableDeclarationState;

	newVariableDeclarationInstance.hydrateAncestors();
	newVariableDeclarationInstance.addSelfToProject(changeSet);
	newVariableDeclarationInstance.initChildren(changeSet);

	changeSet.add(
		newVariableDeclarationInstance,
		ChangeSetEntityChangeType.Added
	);

	// We connect the calls of the writter entity with the new variable declaration
	if (!!writterCaller && ENTRY_POINT_TYPES.includes(writterCaller.type)) {
		(writterCaller as EntryPointEntityState).addCall(
			newVariableDeclarationInstance,
			changeSet
		);
		changeSet.add(writterCaller, ChangeSetEntityChangeType.Affected);
		newVariableDeclarationInstance.addEntryCaller(
			writterCaller as EntryPointEntityState,
			changeSet
		);
	} else if (
		!!writterCaller &&
		PASS_THROUGH_CALLABLE_TYPES.includes(writterCaller.type) &&
		// Except if the variable will not be at the global level
		writterScopeOwner.type !== EntityType.Project
	) {
		(writterCaller as PassThroughCallableEntityState).addSuccessCall(
			newVariableDeclarationInstance,
			changeSet
		);
		changeSet.add(writterCaller, ChangeSetEntityChangeType.Affected);
		newVariableDeclarationInstance.addSuccessCaller(
			writterCaller as PassThroughCallableEntityState,
			changeSet
		);
	}

	const newDataType = from
		.getDataType(changeSet)
		?.clone(changeSet, newVariableDeclarationInstance);

	newDataType?.setParent(newVariableDeclarationInstance, changeSet);
	newDataType?.initChildren(changeSet);

	if (!!newDataType) {
		changeSet.add(newDataType, ChangeSetEntityChangeType.Added);

		newVariableDeclarationInstance.setDataType(newDataType, changeSet);
	}

	return newVariableDeclarationInstance;
}
